/**
 * A "resource" (an activity, article, or video a teacher/student/counselor can open) isn't its own
 * database entity anywhere in this app - it's just an `<a>` tag embedded inside a Topic's rich-text
 * `content` HTML, grouped by whichever `<h1>` heading precedes it (e.g. "Actividades", "Informativo",
 * "Videos"). This parsing convention is the one `TopicContent` (src/components/topic/content.jsx)
 * already established and ships in production - extracted here so a resource search can reuse the
 * exact same rules instead of re-deriving them, and so a change to the convention only has one place
 * to be fixed.
 */

export const RESOURCE_TYPES = ['activities', 'articles', 'media']

/**
 * Classify a topic content section's heading text into one of the known resource types, matching
 * the same loose regexes `TopicContent` uses (headings are free text, not a controlled vocabulary -
 * e.g. Spanish "Actividades"/"Informativo"/"Videos" or English "Activities"/"Articles"/"Media" all
 * match). Anything else is 'unknown' and dropped by callers, same as `TopicContent` does today.
 * @param {string} headingText
 * @returns {'activities'|'articles'|'media'|'unknown'}
 */
export const classifyResourceType = (headingText) => {
  if (/activi/i.test(headingText)) return 'activities'
  if (/artic/i.test(headingText) || /nform/i.test(headingText)) return 'articles'
  if (/graph/i.test(headingText) || /video/i.test(headingText)) return 'media'
  return 'unknown'
}

/**
 * Parse a Topic's `content` HTML into its resource links, grouped by type. Each entry is a plain
 * `{ href, text }` (computed once from the DOM anchor - `href` resolved to an absolute URL, `text`
 * the same as `textContent` - so callers get plain, serializable data instead of holding onto a
 * disposable DOM fragment).
 * @param {string} contentHtml
 * @returns {{activities: Array<{href:string,text:string}>, articles: Array, media: Array, unknown: Array}}
 */
export const extractResourceLinks = (contentHtml) => {
  const linksByResourceType = { activities: [], articles: [], media: [], unknown: [] }
  if (!contentHtml || contentHtml.trim() === '') return linksByResourceType

  const contentFragment = document.createRange().createContextualFragment(contentHtml)

  let currentResourceType = null
  for (const child of contentFragment.children) {
    if (child.tagName.match(/^h\d+/i)) {
      currentResourceType = classifyResourceType(child.textContent)
    } else {
      if (!currentResourceType) continue
      const links = Array.from(child.getElementsByTagName('a'))
      linksByResourceType[currentResourceType].push(...links.map(link => ({ href: link.href, text: link.text })))
    }
  }

  return linksByResourceType
}

/**
 * Which grade/level Topic.tags entry (e.g. 'level:beginner') applies to a STUDENT/PARENT, read off
 * their own current course - mirrors `ActivitiesCategories`'s `handleTopicsStudent`. Returns null
 * when there's no current course or no level tag on it (e.g. ECE/TEACHER never have one - they
 * browse every level for a category, unfiltered).
 * @param {Array<{course: {tags?: string[]}}>} [courses] - CourseContext value
 * @returns {string|null}
 */
export const resolveOwnLevelTag = (courses) => {
  const tags = courses?.[0]?.course?.tags || []
  return tags.find(tag => /^level:/.test(tag)) || null
}
